import { QueryClient, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosRequestConfig } from 'axios';
import { isEmpty } from 'lodash';
import React from 'react';
import { FieldValues, UseFormReset } from 'react-hook-form';
import {
	PerilConfigurationResponse,
	ProductConfigurationResponse,
} from '../api/ConfigApi';
import { handleUnauthorizedResponse } from '../api/http';
import PolicyApi, { getUsePolicyQueryKey } from '../api/PolicyApi';
import ProductApi from '../api/ProductApi';
import ResourcesAPI from '../components/admin/tenant/api/resources';
import { AUTOFILL_ALWAYS_ON_FF } from '../components/dashboard/constants';
import { FORM_NAME } from '../components/fastForm/data/constants';
import {
	getDefaultPolicyValues,
	getQuotePolicyExposuresForm,
	getQuotePolicyForm,
} from '../components/fastForm/formBuilders';
import {
	FormBuilder,
	FormShape,
	SaveFastForm,
} from '../components/fastForm/types';
import draftPolicyUpdateBuilder from '../components/fastForm/updateBuilders/draftPolicyUpdateBuilder';
import { errorToast } from '../components/Toast';
import { CancellationResponse } from '../types/cancellations';
import {
	EndorsementUpdateRequest,
	ExposureConfiguration,
} from '../types/endorsements';
import {
	Exposure,
	ExposureCreateRequest,
	FieldGroupUpdateRequest,
	FieldValuesMap,
	GraceLapseReinstatementResponse,
	PerilCreateRequest,
	Policy2,
	PolicyQuoteResponse,
	PolicyQuoteUpdateRequest,
	PolicyUpdateRequest,
} from '../types/policy';
import {
	buildCloneExposureObj,
	getCurrentUNIXTimestamp,
	getPolicyConfigVersion,
	sortByObjectKey,
} from '../util/util';
import { AuthContext, AuthData, HIDE_PERIL_LIMITS_ENABLE } from './auth';
import { ErrorContext, ErrorData } from './ErrorProvider';

export const PolicyContext = React.createContext<PolicyData>(null);

export const usePolicyContext = () => React.useContext(PolicyContext);

export interface PolicyData {
	fields: FormShape<FormBuilder>;
	quoteUpdateObj: PolicyQuoteUpdateRequest;
	policyUpdateObj: PolicyUpdateRequest;
	endorsementUpdateObj: EndorsementUpdateRequest;
	quotes: PolicyQuoteResponse[];
	policy: Policy2;
	graceLapse?: GraceLapseReinstatementResponse[];
	productConfig: ProductConfigurationResponse;
	selectedQuote: PolicyQuoteResponse;
	selectedExposure?: Exposure;
	selectedCancellation?: CancellationResponse;
	policyViewTimestamp?: string;
	updateFields?: () => Promise<PolicyData>;
	saveFastForm: SaveFastForm<PolicyData>;
	setLoading?: (loading: boolean) => void;
	setQuoteUpdateObj: (
		updateObj: PolicyQuoteUpdateRequest,
	) => Promise<PolicyData>;
	setPolicyUpdateObj: (updateObj: PolicyUpdateRequest) => Promise<PolicyData>;
	setEndorsementUpdateObj: (
		endorsementUpdateObj: EndorsementUpdateRequest,
	) => Promise<PolicyData>;
	setQuotes: (quotes: PolicyQuoteResponse[]) => Promise<PolicyData>;
	setPolicy: (policy: Policy2) => Promise<PolicyData>;
	setProductConfig: (
		productConfig: ProductConfigurationResponse,
	) => Promise<PolicyData>;
	setSelectedExposure: (selectedExposure: Exposure) => Promise<PolicyData>;
	setPolicyViewTimestamp: (policyViewTimestamp: string) => Promise<PolicyData>;
	setSelectedQuote: (selectedQuote: PolicyQuoteResponse) => Promise<PolicyData>;
	updateGroupFieldValue: (
		name: string,
		values: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => Promise<PolicyData>;
	handleRemoveFieldGroup: (
		locator: string,
		index: number,
	) => Promise<PolicyData>;
	updateGroupFieldValueForEndorsement: (
		name: string,
		values: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => any;
	getQuotes: () => Promise<PolicyData>;
	loadData: (
		policyId: string,
		config?: AxiosRequestConfig<any>,
	) => Promise<PolicyData>;
	clear: () => Promise<PolicyData>;
	clearQuoteUpdateObj: () => Promise<PolicyData>;
	cloneExposure: (
		targetExposure: Exposure,
		values: FieldValues,
		reset: UseFormReset<FieldValues>,
		dirtyFields?: { [key: string]: boolean },
	) => Promise<PolicyData>;
	resourcesTimestamps: number[];
}

interface Props {
	authData: AuthData;
	errorData: ErrorData;
	queryClient: QueryClient;
}

class PolicyProvider extends React.Component<Props, PolicyData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			quoteUpdateObj: {},
			policyUpdateObj: {},
			endorsementUpdateObj: {},
			fields: null,
			quotes: null,
			policy: null,
			graceLapse: null,
			productConfig: null,
			selectedQuote: null,
			selectedExposure: null,
			policyViewTimestamp: getCurrentUNIXTimestamp(),
			saveFastForm: this.saveFastForm,
			setQuoteUpdateObj: this.setQuoteUpdateObj,
			setPolicyUpdateObj: this.setPolicyUpdateObj,
			setEndorsementUpdateObj: this.setEndorsementUpdateObj,
			setQuotes: this.setQuotes,
			setPolicy: this.setPolicy,
			setProductConfig: this.setProductConfig,
			setSelectedQuote: this.setSelectedQuote,
			updateGroupFieldValue: this.updateGroupFieldValue,
			handleRemoveFieldGroup: this.handleRemoveFieldGroup,
			updateGroupFieldValueForEndorsement:
				this.updateGroupFieldValueForEndorsement,
			setSelectedExposure: this.setSelectedExposure,
			setPolicyViewTimestamp: this.setPolicyViewTimestamp,
			getQuotes: this.getQuotes,
			loadData: this.loadData,
			clear: this.clear,
			clearQuoteUpdateObj: this.clearQuoteUpdateObj,
			updateFields: this.updateFields,
			cloneExposure: this.cloneExposure,
			resourcesTimestamps: [],
		};
	}

	public render() {
		return (
			<PolicyContext.Provider value={this.state}>
				{this.props.children}
			</PolicyContext.Provider>
		);
	}

	private setQuoteUpdateObj = (updateObj: PolicyQuoteUpdateRequest) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ quoteUpdateObj: updateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPolicyUpdateObj = (updateObj: PolicyUpdateRequest) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ policyUpdateObj: updateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setEndorsementUpdateObj = (
		endorsementUpdateObj: EndorsementUpdateRequest,
	) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ endorsementUpdateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setQuotes = (quotes: PolicyQuoteResponse[]) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ quotes }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setGraceLapse = (graceLapse: GraceLapseReinstatementResponse[]) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ graceLapse }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPolicy = (policy: Policy2) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ policy }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setProductConfig = (productConfig: ProductConfigurationResponse) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ productConfig }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setResourcesTimestamps = (resourcesTimestamps: number[]) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ resourcesTimestamps }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSelectedExposure = (selectedExposure: Exposure) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ selectedExposure }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPolicyViewTimestamp = (policyViewTimestamp: string) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ policyViewTimestamp }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSelectedQuote = (selectedQuote: PolicyQuoteResponse) => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ selectedQuote }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private updateGroupFieldValue = (
		name: string,
		fieldGroupValues: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => {
		const { quoteUpdateObj, policyUpdateObj, selectedQuote } = this.state;
		const updateObject = selectedQuote ? quoteUpdateObj : policyUpdateObj;
		const setUpdateObject = selectedQuote
			? this.setQuoteUpdateObj
			: this.setPolicyUpdateObj;

		const updateFieldGroups = updateObject.updateFieldGroups || [];
		let objectIndex: number = null;

		const updateRequest = updateFieldGroups.find((req, idx) => {
			if (req.fieldGroupLocator === fieldGroupLocator) {
				objectIndex = idx;
			}

			return req.fieldGroupLocator === fieldGroupLocator;
		});

		const newUpdate = {
			fieldName: name,
			fieldGroupLocator,
			fieldValues: fieldGroupValues,
		} as FieldGroupUpdateRequest;

		// update field exists but locator doesn't match.
		if (updateRequest) {
			updateFieldGroups[objectIndex] = newUpdate;
		} else {
			updateFieldGroups.push(newUpdate);
		}

		updateObject.updateFieldGroups = updateFieldGroups;
		return setUpdateObject(updateObject);
	};

	private handleRemoveFieldGroup = async (locator: string) => {
		const { authData } = this.props;
		const {
			selectedQuote,
			getQuotes,
			quoteUpdateObj,
			setQuoteUpdateObj,
			policyUpdateObj,
			setPolicyUpdateObj,
			policy,
		} = this.state;
		const updateObject = selectedQuote ? quoteUpdateObj : policyUpdateObj;
		const setUpdateObject = selectedQuote
			? setQuoteUpdateObj
			: setPolicyUpdateObj;

		// remove any updates to the group that is being removed
		const updatedFieldGroups = updateObject.updateFieldGroups.filter(
			(fg) => fg.fieldGroupLocator !== locator,
		);
		// if there are no more updates to field groups, remove them from updateObject
		if (updatedFieldGroups.length) {
			updateObject.updateFieldGroups = updatedFieldGroups;
		} else {
			delete updateObject.updateFieldGroups;
		}
		await setUpdateObject(updateObject);
		// Send update request to remove field group
		const removeFieldGroupsReq = { removeFieldGroups: [locator] };

		try {
			if (selectedQuote) {
				await PolicyApi.updateQuote(
					authData.auth.authorizationToken,
					selectedQuote.locator,
					removeFieldGroupsReq,
				);
				return getQuotes();
			} else {
				const response = await PolicyApi.updatePolicy(
					authData.auth.authorizationToken,
					policy.locator,
					removeFieldGroupsReq,
				);
				return this.setPolicy(response.data);
			}
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			return null;
		}
	};

	private updateGroupFieldValueForEndorsement = (
		name: string,
		fieldGroupValues: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => {
		const { endorsementUpdateObj } = this.state;

		if (fieldGroupLocator) {
			// updateFieldGroups

			const updateFieldGroups = endorsementUpdateObj.updateFieldGroups || [];
			let updateRequest = updateFieldGroups.find(
				(req) => req.fieldGroupLocator === fieldGroupLocator,
			);
			let updateIndex = updateFieldGroups.findIndex(
				(req) => req.fieldGroupLocator === fieldGroupLocator,
			);
			updateIndex = updateIndex === -1 ? 0 : updateIndex;

			const newUpdate = {
				fieldGroupLocator,
				fieldName: name,
				// fieldValues: fieldGroupValues,
			} as FieldGroupUpdateRequest;

			updateRequest = updateRequest || newUpdate;
			updateRequest.fieldValues = fieldGroupValues;

			// update field exists but locator doesn't match.
			if (updateRequest) {
				updateFieldGroups[updateIndex] = updateRequest;
			}

			endorsementUpdateObj.updateFieldGroups = updateFieldGroups;
			this.setEndorsementUpdateObj(endorsementUpdateObj);
		} else {
			// add field groups
			this.setEndorsementUpdateObj(endorsementUpdateObj);
		}
	};

	private updateFields = () => {
		const isLegacyApplication =
			!this.state.policy.issuedTimestamp && !this.state.quotes.length;
		const fields = isLegacyApplication
			? null
			: {
					...getQuotePolicyForm({
						policyData: this.state,
						authData: this.props.authData,
					}),
					...getQuotePolicyExposuresForm({
						policyData: this.state,
						authData: this.props.authData,
					}),
			  };
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState({ fields }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private getQuotes = async () => {
		const { authData } = this.props;
		try {
			const response = await PolicyApi.getQuotes(
				authData.auth.authorizationToken,
				this.state.selectedQuote.policyLocator,
			);
			const quotes = response.data;
			const selectedQuote = quotes.find(
				(quote: PolicyQuoteResponse) => quote.selected,
			);
			await this.setSelectedQuote(selectedQuote);

			return this.setQuotes(quotes);
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			errorToast('Failed to fetch necessary data');
			return null;
		}
	};

	private loadData = async (
		policyId: string,
		config?: AxiosRequestConfig<any>,
	) => {
		const { authData, queryClient } = this.props;
		if (!authData) {
			return Promise.reject();
		}
		if (!authData.auth) {
			return Promise.reject();
		}

		try {
			const policyPromise = await PolicyApi.getPolicy(
				authData.auth.authorizationToken,
				policyId,
				config,
			);
			const policy = policyPromise.data;

			let quotes;
			const policyQueryKey = getUsePolicyQueryKey(
				policyId,
				authData.auth.authorizationToken,
			);
			queryClient.setQueryData(policyQueryKey, policy);

			// set policy view timestamp if policy is issued
			if (policy.issuedTimestamp) {
				const todayTimestamp = new Date().getTime().toString();
				// use policy startTimestamp if viewing prior to policy's start (QE-3844)
				const initialViewTimestamp =
					todayTimestamp < policy.originalContractStartTimestamp
						? policy.originalContractStartTimestamp
						: todayTimestamp;
				await this.setPolicyViewTimestamp(initialViewTimestamp);
			}

			// For policies that don't support quotes, the response will be a 404

			// Workaround for policies that have issues loading quotes (e.g. having tons of financial transactions).
			// User can skip the loading of quotes by providing a special queryParam in the URL
			const queryParams = new URLSearchParams(window.location.search);
			if (!queryParams.has('skipLoadingQuotes')) {
				try {
					const quotesPromise = await PolicyApi.getQuotes(
						authData.auth.authorizationToken,
						policyId,
						config,
					);
					quotes = quotesPromise.data;
					const selectedQuote = quotes.find(
						(quote: PolicyQuoteResponse) => quote.selected,
					);
					await this.setSelectedQuote(selectedQuote);
					// set policy view timestamp if policy has not issued yet
					if (!policy.issuedTimestamp && selectedQuote) {
						await this.setPolicyViewTimestamp(
							selectedQuote.contractStartTimestamp,
						);
						if (selectedQuote.state === 'draft') {
							const timestamps = await ResourcesAPI.getResourcesTimestamps(
								policy.productName,
								authData.auth.authorizationToken,
							);
							await this.setResourcesTimestamps(timestamps);
						}
					}
				} catch (error) {
					if (axios.isCancel(error)) {
						throw error;
					} else {
						console.error(error);
					}
				}
			}

			// get product config
			const pvEnabled =
				authData.auth.configProperties[
					'property.product.versioning.m2.enabled'
				] ||
				authData.auth.configProperties[
					'property.product.versioning.with.repair.enabled'
				];
			const policyVersion = getPolicyConfigVersion(policy);
			let configs = null;
			if (pvEnabled) {
				configs = await ProductApi.getProductsWithVersion(
					authData.auth.authorizationToken,
					policyVersion,
				);
			} else {
				configs = await ProductApi.getProducts(
					authData.auth.authorizationToken,
				);
			}

			const graceAndLapse = await PolicyApi.getGraceLapseAndReinstatementInfo(
				authData.auth.authorizationToken,
				policy.locator,
				config,
			);
			const productConfig = configs.data.find(
				(config: ProductConfigurationResponse) =>
					config.name === policy.productName,
			);
			// set state variables
			await this.setPolicy(policy);
			await this.setQuotes(quotes);
			await this.setGraceLapse(graceAndLapse.data);
			await this.setProductConfig(productConfig);
			return this.updateFields();
		} catch (error) {
			if (axios.isCancel(error)) {
				throw error;
			} else {
				await handleUnauthorizedResponse(error, authData);
				return null;
			}
		}
	};

	private clear = () => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState(
				{
					quotes: null,
					policy: null,
					selectedQuote: null,
					quoteUpdateObj: {},
					selectedExposure: null,
					productConfig: null,
				},
				() => {
					resolve(this.state);
				},
			);
		});
		return promise;
	};

	private clearQuoteUpdateObj = () => {
		const promise = new Promise<PolicyData>((resolve) => {
			this.setState(
				{
					quoteUpdateObj: null,
				},
				() => {
					resolve(this.state);
				},
			);
		});
		return promise;
	};

	private saveFastForm: SaveFastForm<PolicyData> = async (
		values,
		dirtyFields,
		reset,
		options,
	) => {
		let updatedPolicyData = {} as PolicyData;

		const {
			loadData,
			quoteUpdateObj,
			policyUpdateObj,
			setQuoteUpdateObj,
			setPolicyUpdateObj,
			selectedQuote,
			fields,
			policy,
			policy: { locator },
		} = this.state;
		const { authorizationToken, configProperties } = this.props.authData.auth;
		const hidePerilLimitsEnabled =
			this.props.authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ??
			false;
		const formPayload = draftPolicyUpdateBuilder(
			dirtyFields,
			values,
			fields,
			'policyForm',
			options,
			this.props.authData.auth.timezone,
			hidePerilLimitsEnabled,
		);
		const autofillButtonAlwaysEnabled = configProperties[AUTOFILL_ALWAYS_ON_FF];

		const otherUpdates = selectedQuote ? quoteUpdateObj : policyUpdateObj;
		const updateObject = { ...formPayload, ...otherUpdates };

		if (options?.runAutofill) {
			// default specified autofill sequence will run when autofill array is null
			updateObject.autofill = null;

			// if AUTOFILL_ALWAYS_ON_FF flag enabled AND there are no changes made
			// include policyStart value in payload so request is not rejected by BE for being empty
			if (autofillButtonAlwaysEnabled && isEmpty(dirtyFields)) {
				const policyStart = updateObject.policyStartTimestamp
					? updateObject.policyStartTimestamp
					: selectedQuote
					? selectedQuote.contractStartTimestamp
					: policy.originalContractStartTimestamp;
				updateObject.policyStartTimestamp = policyStart;
			}
		} else {
			// for regular updates, send an empty array to signify to run no autofills
			updateObject.autofill = [];
		}

		if (selectedQuote) {
			await PolicyApi.updateQuote(
				authorizationToken,
				selectedQuote.locator,
				updateObject,
			);
		} else {
			await PolicyApi.updatePolicy(authorizationToken, locator, updateObject);
		}
		updatedPolicyData = await loadData(locator);

		selectedQuote ? setQuoteUpdateObj({}) : setPolicyUpdateObj({});

		const defaultPolicyValues = getDefaultPolicyValues(
			updatedPolicyData.fields,
			this.props.authData,
		);

		reset(defaultPolicyValues);

		return updatedPolicyData;
	};

	private cloneExposure = async (
		targetExposure: Exposure,
		values: FieldValues,
		reset: UseFormReset<FieldValues>,
		dirtyFields?: { [key: string]: boolean },
	) => {
		// Note: clone endorsement is applicable for QUOTE only
		if (!this.state.selectedQuote || !targetExposure) {
			throw new Error('Unexpected clone operation');
		}

		let result = {} as PolicyData;
		const {
			fields,
			selectedQuote,
			policy: { locator },
			loadData,
			quoteUpdateObj,
		} = this.state;
		const { authorizationToken } = this.props.authData.auth;
		const hidePerilLimitsEnabled =
			this.props.authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ??
			false;
		const pendingChanges = draftPolicyUpdateBuilder(
			dirtyFields,
			values,
			fields,
			FORM_NAME.POLICY_FORM,
			{},
			this.props.authData.auth.timezone,
		);
		const pendingExposureChanges = pendingChanges?.updateExposures?.find(
			(e) => e.exposureLocator === targetExposure.locator,
		);
		const cloneChanges = buildCloneExposureObj(
			pendingExposureChanges,
			fields,
			targetExposure,
			hidePerilLimitsEnabled,
		);

		const updateObj = {
			...pendingChanges,
			...quoteUpdateObj,
			addExposures: [cloneChanges],
		} as PolicyQuoteUpdateRequest;

		await PolicyApi.updateQuote(
			authorizationToken,
			selectedQuote.locator,
			updateObj,
		);
		result = await loadData(locator);
		const defaultPolicyValues = getDefaultPolicyValues(
			result.fields,
			this.props.authData,
		);
		reset(defaultPolicyValues);
		return result;
	};
}

export function withPolicyContext<T>(Component: any) {
	return (props: Omit<T, 'errorData' | 'policyData' | 'authData'>) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									authData={authData}
									policyData={policyData}
									errorData={errorData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export function getNewExposure(
	exposureConfig: ExposureConfiguration,
	hidePerilLimitsEnabled?: boolean,
): ExposureCreateRequest {
	return {
		exposureName: exposureConfig.name,
		perils: sortByObjectKey(exposureConfig.perils, 'displayName').map(
			(peril: PerilConfigurationResponse) =>
				getNewPeril(peril, hidePerilLimitsEnabled),
		),
	};
}

export function getNewPeril(
	perilConfig: PerilConfigurationResponse,
	hidePerilLimitsEnabled?: boolean,
): PerilCreateRequest {
	// if hidePerilLimitsEnabled FF is true, don't include any coverage limit attributes from config
	return hidePerilLimitsEnabled
		? {
				name: perilConfig.name,
				fieldValues: {},
				fieldGroups: [],
		  }
		: {
				name: perilConfig.name,
				fieldValues: {},
				fieldGroups: [],
				indemnityPerItem: perilConfig.indemnityPerItem || null,
				indemnityPerEvent: perilConfig.indemnityPerEvent || null,
				indemnityInAggregate: perilConfig.indemnityInAggregate || null,
				lumpSumPayment: `${perilConfig.lumpSumPayment || null}`,
				deductible: `${perilConfig.deductible || null}`,
		  };
}

function UseQuery(props: {
	children: (queryClient: QueryClient) => JSX.Element;
}) {
	return props.children(useQueryClient());
}

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<UseQuery>
			{(queryClient) => (
				<AuthContext.Consumer>
					{(authData) => (
						<Component
							{...props}
							authData={authData}
							queryClient={queryClient}
						/>
					)}
				</AuthContext.Consumer>
			)}
		</UseQuery>
	);
}
export default withContext(PolicyProvider);
